import Footer from "@/components/Common/Footer";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import Logo from "@/components/ui/logo";
import { useNavigate, useRouterState } from "@tanstack/react-router";
import { useAuth } from "@/features/auth/hooks/useAuth";

interface LandingNavbarProps {
  isScrolled?: boolean;
  getInTouch?: boolean;
  onGetInTouchClick?: () => void;
}

export default function LandingNavbar({
  isScrolled = false,
  getInTouch = false,
  onGetInTouchClick,
}: LandingNavbarProps) {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();

  const pathname = useRouterState({
    select: (s) => s.location.pathname,
  });

  const handleGetInTouch = (e: React.MouseEvent) => {
    e.stopPropagation();

    // Already on landing page → just scroll
    if (pathname === "/") {
      onGetInTouchClick?.();
      return;
    }

    // From another page → redirect to "/" with param
    navigate({
      to: "/",
      search: { section: "section5" },
    });
  };

  const handleLogoClick = () => {
    navigate({ to: "/", search: { section: undefined } });
  };

  const handleAuthButtonClick = (e: React.MouseEvent) => {
    e.stopPropagation();

    if (isAuthenticated) {
      navigate({ to: "/keyword-monitoring" });
    } else {
      navigate({ to: "/login" });
    }
  };

  return (
    <nav
      className={cn(
        "sticky top-0 right-0 left-0 z-50 flex w-full items-center justify-between px-4 py-4 transition-all duration-300 sm:px-8 sm:py-5 md:px-16 lg:px-24 xl:px-[200px]",
        isScrolled
          ? "bg-white/20 shadow-xs backdrop-blur-lg backdrop-saturate-150"
          : "bg-neutral-50",
      )}
    >
      <div onClick={handleLogoClick} className="hover:cursor-pointer">
        <Logo />
      </div>

      <div className="flex flex-row items-center gap-3 sm:gap-4 md:gap-6">
        {getInTouch && (
          <h3
            className="md:text-md text-muted hidden text-sm font-bold hover:cursor-pointer sm:block"
            onClick={handleGetInTouch}
          >
            Get in touch
          </h3>
        )}

        <Button
          className="!text-muted w-[100px] text-xs sm:w-[120px] sm:text-sm md:w-[140px] md:text-base"
          variant="outline"
          onClick={handleAuthButtonClick}
        >
          {isAuthenticated ? "Go back to app" : "Login"}
        </Button>
      </div>
    </nav>
  );
}

export const LandingFooter = () => {
  return (
    <footer className="top-0 right-0 left-0 z-50 flex w-full items-center justify-between bg-neutral-50 px-4 py-4 sm:px-8 sm:py-5 md:px-16 lg:px-24 xl:px-[200px]">
      <Logo />
      <div className="flex flex-row items-center gap-3 sm:gap-4 md:gap-6">
        <Footer />
      </div>
    </footer>
  );
};
